import React, { useEffect, useState, useRef } from 'react';
/**
 * OrderingRenderer — Drag-to-reorder items into correct sequence.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 * Mobile: Uses framer-motion Reorder with native touch support.
 * touch-action: none prevents page scroll during drag.
 */
import { Reorder } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { GripVertical } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
interface OrderItem {
  id: number;
  content: string;
}
/** Shuffle ensuring result differs from correctOrder. */
function shuffleAwayFromCorrect(items: OrderItem[], correctOrder: number[]): OrderItem[] {
  if (items.length <= 1) return [...items];
  for (let attempt = 0; attempt < 20; attempt++) {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const ids = shuffled.map(item => item.id);
    const matches = correctOrder.length > 0 && ids.length === correctOrder.length && ids.every((id, idx) => id === correctOrder[idx]);
    if (!matches) return shuffled;
  }
  return [...items].reverse();
}
export function OrderingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const orderingData = config.ordering || config;
  const items: string[] = orderingData.items || [];
  const correctOrder: number[] = orderingData.correctOrder || [];
  const prevValidateRef = useRef(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [order, setOrder] = useState(() => {
    const mapped = items.map((item, index) => ({
      id: index,
      content: item
    }));
    return shuffleAwayFromCorrect(mapped, correctOrder);
  });
  useEffect(() => {
    const mapped = items.map((item, index) => ({
      id: index,
      content: item
    }));
    setOrder(shuffleAwayFromCorrect(mapped, correctOrder));
    setHasInteracted(false);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(hasInteracted);
  }, [hasInteracted, onReadyChange, question.id]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(order.map(item => item.id).join(','));
    }
  }, [shouldValidate, order, onAnswer]);
  // Guard: no items — rendered AFTER all hooks
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-7510">
        <p className="text-amber-700 font-bold" data-id="element-7511">⚠️ Geen items beschikbaar</p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-7512">
          Deze vraag heeft geen geldige volgordedata.
        </p>
      </div>;
  }
  const handleReorder = (newOrder: OrderItem[]) => {
    setOrder(newOrder);
    if (!hasInteracted) setHasInteracted(true);
  };
  return <div className="w-full max-w-lg mx-auto" data-id="element-7513">
      <p className="text-center text-slate-500 text-sm mb-4" data-id="element-7514">
        <span className="hidden md:inline" data-id="element-7515">
          Sleep de blokken in de juiste volgorde
        </span>
        <span className="md:hidden" data-id="element-7516">
          Houd ingedrukt en sleep in de juiste volgorde
        </span>
      </p>
      <Reorder.Group axis="y" values={order} onReorder={handleReorder} className="space-y-3" style={{
      touchAction: 'none'
    }} data-id="element-7517">
        {order.map((item, index) => <Reorder.Item key={item.id} value={item} className="bg-white border-2 border-slate-200 rounded-xl p-4 flex items-center gap-4 shadow-sm cursor-grab active:cursor-grabbing select-none" style={{
        touchAction: 'none'
      }} whileDrag={{
        scale: 1.03,
        boxShadow: '0 8px 25px rgba(99, 102, 241, 0.2)',
        borderColor: 'rgb(129, 140, 248)',
        zIndex: 50
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} data-id="element-7518">
            <div className="flex-shrink-0 w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400" data-id="element-7519">
              <GripVertical className="w-5 h-5 md:w-4 md:h-4" data-id="element-7520" />
            </div>
            <span className="text-lg font-medium text-slate-700 flex-1" data-id="element-7521">
              <LatexRenderer content={item.content} data-id="element-7522" />
            </span>
            <span className="text-xs font-bold text-slate-300 tabular-nums w-5 text-right" data-id="element-7523">
              {index + 1}
            </span>
          </Reorder.Item>)}
      </Reorder.Group>
    </div>;
}